/**
 * Curation for the provisioning-replay log — the rail's LAST line of defence
 * against ops language reaching a prospect.
 *
 * The replay body is the arc's "watch it do the work" beat, and the doctrine
 * for it is one sentence: every line a prospect reads must be a check in
 * BUSINESS language ("Read q3-nav-bridge.csv", "Built Q3 2026 Platform
 * Review"). What the platform's tee historically stored was the raw pipeline
 * tail, and an audited live demo showed all of it on screen: twenty
 * "waiting on ['review.quarterly-deck'] (2.3 min elapsed)" heartbeats, seat
 * routing ("Duncan Reddaway: landing_path -> /w/…"), an idempotency note
 * ("content.comments cleared after seeding — re-seeding is not idempotent"),
 * and a cost warning ("this spends demo LLM budget"). The platform now
 * curates at the tee for NEW authoring runs; this module gives every
 * already-provisioned demo the same log without a re-author, and catches
 * anything a future emitter leaks.
 *
 * Shape rules:
 *  - known ops noise is DROPPED;
 *  - known work events are TRANSLATED into the layman phrasing;
 *  - anything unrecognised passes through untouched — future platform lines
 *    are assumed curated at their emitter, and this must never eat them.
 *
 * No "✓" in the text: the rail already draws a check icon per line.
 */

import type { FirstRunReplayLine } from "./payload";

/** Lines that are ops plumbing, not work — dropped whole. Matched against the
 *  line with any wrapping quotes stripped (the tee stores some repr()-style). */
const DROP_PATTERNS: readonly RegExp[] = [
  /^waiting on /, // poll heartbeats — a spinner rendered as text
  /^reconciled the workspace/, // control-plane step
  /landing_path/, // seat routing, with internal URLs
  /^content\./, // seeding/idempotency housekeeping
  /LLM budget/, // operator cost warning
  /^meets the content bar$/, // internal QA gate phrasing
  /^all runs complete/, // redundant beside the completed-after line
];

const INGESTED = /^ingested (\S+) (?:->|→) \S+$/;
const COMPILED = /^compiled (.+) \((\w+), \d+ version\(s\)\)$/;
const COMPLETED = /^[\w.-]+: completed after ([\d.]+) min$/;

/** One line, curated: the translated line, the original, or `null` to drop. */
export function curateReplayLine(raw: string): string | null {
  // The tee stores a few lines as their repr — quoted. Unwrap for matching
  // and for display; the quotes carry nothing. A trailing literal "✓" is
  // dropped too — the rail draws its own check icon per line, and a source
  // that embeds one would render a doubled tick.
  const line = raw
    .replace(/^"(.*)"$/, "$1")
    .replace(/\s*✓\s*$/, "")
    .trim();
  if (!line) return null;
  for (const pattern of DROP_PATTERNS) {
    if (pattern.test(line)) return null;
  }
  const ingestedFile = INGESTED.exec(line)?.[1];
  // The internal workspace slug after "->" means nothing to a prospect.
  if (ingestedFile) return `Read ${ingestedFile}`;
  const compiled = COMPILED.exec(line);
  if (compiled?.[1] && compiled[2]) return `Built ${compiled[1]} (${compiled[2].toUpperCase()})`;
  const completedMin = COMPLETED.exec(line)?.[1];
  // The workflow id prefix is dropped — "review.quarterly-deck" is config,
  // not language. The minutes stay: they are the real cost of real work.
  if (completedMin) return `Analysis complete — ${completedMin} min of real work`;
  return line;
}

/** The whole log, curated, order preserved. */
export function curateReplayLines(
  lines: readonly FirstRunReplayLine[],
): FirstRunReplayLine[] {
  const out: FirstRunReplayLine[] = [];
  for (const entry of lines) {
    const line = curateReplayLine(entry.line);
    if (line !== null) out.push({ t: entry.t, line });
  }
  return out;
}
